import { CheckIcon, InfoIcon, ProhibitIcon, ScalesIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { useConfig } from '@entities/config/api';
import { money } from '@shared/lib/money';
import { percentFromBps } from '@shared/lib/percent';
import { Avatar, Container, CtaBand, Skeleton } from '@shared/ui';
import section from '@shared/ui/PublicSection/publicSection.module.css';
import { Reveal } from '@shared/ui/Reveal/Reveal';

import styles from './AboutPage.module.css';

/**
 * Ступени географии поиска (MVP_PRD §43): своя земля → соседние → вся
 * страна, и расширение — только по согласию пользователя.
 *
 * Прежде на этом месте стоял таймлайн семи шагов заказа. Тексты заказчика от
 * 29.10.2026 рассказывают путь иначе, и таймлайн показывает теперь ту его
 * часть, которая действительно идёт по ступеням.
 */
const SEARCH_STEPS = ['region', 'wider', 'germany'] as const;

const DEALER_PARAGRAPHS = [
  'professional',
  'liability',
  'term',
  'scope',
  'private',
  'guarantee',
] as const;

/**
 * Три части рекомендации. Исключённые варианты — серая иконка, как прежний
 * список границ: это сведение о подборе, а не тревога (DESIGN_SYSTEM §2.4).
 */
const RESULT = [
  { key: 'choice', icon: CheckIcon },
  { key: 'alternatives', icon: ScalesIcon },
  { key: 'excluded', icon: ProhibitIcon },
] as const;

const NEGOTIATE_PARAGRAPHS = ['mandate', 'offer', 'kept'] as const;
const SUPPORT_PARAGRAPHS = ['visit', 'extraChecks', 'basis'] as const;

export function AboutPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  // Цена услуги и условия вознаграждения — только из `GET /config`: хардкод
  // цены в интерфейсе и локалях запрещён (MVP_TECH_RFC §25).
  const { data: config, isPending } = useConfig();

  const top4Price = config
    ? money(config.payments.price_cents, config.payments.currency, locale)
    : null;
  const successFee = config?.success_fee.enabled
    ? t('pricing.successFee', {
        rate: percentFromBps(config.success_fee.rate_bps, locale),
        flat: money(config.success_fee.flat_cents, config.payments.currency, locale),
      })
    : null;

  return (
    <>
      {/* 1. Заголовок с красным фрагментом «CarViQ.DE»: публичная страница
          (DESIGN_SYSTEM §3.3). */}
      <section className={styles.hero}>
        <Container className={styles.heroInner}>
          <div>
            <span className={section.eyebrow}>{t('about.eyebrow')}</span>
            <h1 className={styles.title}>
              <em className={styles.titleAccent}>{t('brand.name')}</em> {t('about.titleAfter')}
            </h1>
            <p className={styles.subtitle}>{t('about.subtitle')}</p>
          </div>

          {/*
            Врезка о Karl. Прежде здесь стояла цитата продукта; теперь — что
            делает помощник. Маркировка AI стоит вплотную к изображению Karl, а
            не под абзацем: подпись относится к лицу (DESIGN_SYSTEM §5.4).
          */}
          <Reveal className={styles.statement}>
            <div className={styles.statementHead}>
              <Avatar variant="karl" size="md" />
              <p className={styles.statementSource}>
                {t('app.karl.name')} · {t('app.karl.role')}
              </p>
            </div>
            <p className={styles.statementText}>{t('about.karl')}</p>
          </Reveal>
        </Container>
      </section>

      {/* 2. Поиск рядом: таймлайн с синими узлами — ступени географии. */}
      <section className={section.sectionMuted} id="search">
        <Container>
          <Reveal className={section.intro}>
            <h2 className={section.title}>{t('about.search.title')}</h2>
            <p className={section.text}>{t('about.search.text')}</p>
          </Reveal>

          <ol className={styles.timeline}>
            {SEARCH_STEPS.map((step, index) => (
              <Reveal as="li" key={step} index={index} className={styles.stage}>
                <span className={`${styles.stageIndex} tabular`} aria-hidden="true">
                  {index + 1}
                </span>
                <h3 className={styles.stageTitle}>
                  <span className="visually-hidden">
                    {t('about.search.stepLabel', { number: index + 1 })}{' '}
                  </span>
                  {t(`about.search.steps.${step}.title`)}
                </h3>
                <p className={styles.stageText}>{t(`about.search.steps.${step}.text`)}</p>
              </Reveal>
            ))}
          </ol>

          <Reveal as="p" className={styles.note}>
            {t('about.search.note')}
          </Reveal>
        </Container>
      </section>

      {/* 3. Почему автохаусы: связный текст о Gewährleistung, а не список —
          его читают подряд. */}
      <section className={section.section} id="dealers">
        <Container className={styles.split}>
          <Reveal>
            <h2 className={section.title}>{t('about.dealers.title')}</h2>
          </Reveal>
          <Reveal className={styles.prose} index={1}>
            {DEALER_PARAGRAPHS.map((key) => (
              <p key={key}>{t(`about.dealers.paragraphs.${key}`)}</p>
            ))}
          </Reveal>
        </Container>
      </section>

      {/* 4. Анализ и рекомендация: три части рекомендации — карточкой. */}
      <section className={section.section} id="analysis">
        <Container className={styles.split}>
          <Reveal>
            <h2 className={section.title}>{t('about.analysis.title')}</h2>
          </Reveal>
          <Reveal className={styles.prose} index={1}>
            <p>{t('about.analysis.paragraphs.start')}</p>
            <p>{t('about.analysis.paragraphs.compare')}</p>

            <div className={styles.resultCard}>
              <h3 className={styles.resultTitle}>{t('about.analysis.resultTitle')}</h3>
              <ul className={styles.resultList}>
                {RESULT.map(({ key, icon: Icon }) => (
                  <li key={key} data-kind={key}>
                    <Icon size={18} weight="bold" aria-hidden="true" />
                    <span>
                      <strong>{t(`about.analysis.result.${key}.title`)}</strong>
                      {' — '}
                      {t(`about.analysis.result.${key}.text`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <p>{t('about.analysis.after.missing')}</p>
            <p>{t('about.analysis.after.explain')}</p>
          </Reveal>
        </Container>
      </section>

      {/*
        5. Стоимость по этапам. Три карточки равноправны по смыслу — это этапы
        одного пути, — но цена у каждой своя: Top-4 не выглядит ценой всей
        работы до покупки, а рядом с ним стоят условия вознаграждения
        (DESIGN_SYSTEM §8).
      */}
      <section className={section.section} id="pricing">
        <Container>
          <Reveal className={section.intro}>
            <h2 className={section.title}>{t('about.pricing.title')}</h2>
          </Reveal>

          <ol className={styles.plans}>
            <Reveal as="li" className={styles.plan}>
              <h3 className={styles.planName}>{t('about.pricing.free.name')}</h3>
              <p className={`${styles.planPrice} tabular`}>{t('about.pricing.free.price')}</p>
              <p className={styles.planText}>{t('about.pricing.free.text')}</p>
            </Reveal>

            <Reveal as="li" className={styles.plan} index={1}>
              <h3 className={styles.planName}>{t('about.pricing.top4.name')}</h3>
              <p className={`${styles.planPrice} tabular`}>
                {top4Price ? (
                  <>
                    {top4Price}{' '}
                    <span className={styles.planPriceNote}>
                      {t('about.pricing.top4.priceNote')}
                    </span>
                  </>
                ) : isPending ? (
                  <Skeleton width="7rem" height="1.75rem" />
                ) : (
                  t('about.pricing.top4.priceUnknown')
                )}
              </p>
              <p className={styles.planText}>{t('about.pricing.top4.text')}</p>
            </Reveal>

            <Reveal as="li" className={styles.plan} index={2}>
              <h3 className={styles.planName}>{t('about.pricing.support.name')}</h3>
              <p className={`${styles.planPrice} tabular`}>{t('about.pricing.support.price')}</p>
              <p className={styles.planText}>{t('about.pricing.support.text')}</p>
              {successFee ? <p className={styles.planFee}>{successFee}</p> : null}
            </Reveal>
          </ol>
        </Container>
      </section>

      {/*
        6. Переговоры и подготовка к покупке — две карточки рядом, а под ними
        граница услуги: выбор и договор остаются за покупателем. Она не
        мелкий шрифт: обещание без границы — половина правды (CLAUDE.md §2).
      */}
      <section className={section.section} id="support">
        <Container>
          <div className={styles.scope}>
            <Reveal className={styles.scopeCard}>
              <h2 className={styles.scopeTitle}>{t('about.negotiate.title')}</h2>
              {NEGOTIATE_PARAGRAPHS.map((key) => (
                <p key={key} className={styles.scopeText}>
                  {t(`about.negotiate.paragraphs.${key}`)}
                </p>
              ))}
            </Reveal>

            <Reveal className={styles.scopeCard} index={1}>
              <h2 className={styles.scopeTitle}>{t('about.support.title')}</h2>
              {SUPPORT_PARAGRAPHS.map((key) => (
                <p key={key} className={styles.scopeText}>
                  {t(`about.support.paragraphs.${key}`)}
                </p>
              ))}
            </Reveal>
          </div>

          <Reveal as="p" className={styles.boundary}>
            <InfoIcon size={20} weight="regular" aria-hidden="true" />
            <span>{t('about.boundary')}</span>
          </Reveal>
        </Container>
      </section>

      {/* 7. Финальный призыв: тёмная полоса, её кнопка — единственная красная
          кнопка страницы (DESIGN_SYSTEM §5.1). */}
      <CtaBand
        title={t('about.final.title')}
        text={t('about.final.text')}
        action={t('about.final.action')}
      />
    </>
  );
}
